"use client";

import { cloneElement, isValidElement, type ReactElement, type ReactNode, useMemo } from "react";
import type { BaseDataPoint } from "../../../utils/charts";
import type { AssembledSeries } from "../../../utils/timeSeriesTiles";
import { EmptyState } from "../../EmptyState";
import { flattenChartChildren } from "../flattenChartChildren";
import { TiledTimeSeriesExplorer, type TiledTimeSeriesPanel } from "../TiledTimeSeriesExplorer";
import { useChartSourceHost, useChartSourceResolver } from "./ChartSourceProvider";
import { parseInstant, type ResolvedChartSource, type StreamingChartSource } from "./chart-source-types";
import { useChartFetchBudget, withFetchBudget } from "./chartFetchBudget";

/** The stream a series child draws, named on the child as `seriesKey`. */
const DEFAULT_SERIES_KEY = "line";

/**
 * A series child in a streaming chart names its stream instead of carrying data.
 *
 * `<AreaSeries seriesKey="net" label="Net demand" />` — no `data` prop, because
 * the points do not exist until the reader looks at a window. Everything else
 * about the child (colour, label, stroke width, baseline) is unchanged, which is
 * what lets the same primitives serve both sources.
 */
interface SeriesChildProps {
  seriesKey?: string;
  data?: BaseDataPoint[];
  label?: string;
  /**
   * Relabel this series from the points once they arrive.
   *
   * ── Why the chart resolves this rather than the caller ────────────────────────
   * Some labels are a fact about the DATA, not about the series: whether the
   * points drawn came from one basis or another, whether coverage is partial.
   * That answer does not exist until a window is fetched, so no static string
   * can carry it — and the caller cannot compute it either, because the
   * assembled streams only exist inside this render.
   *
   * So a child may hand up a pure function of (its own points, every stream)
   * and get its label back. The FUNCTION is supplied by whoever renders the
   * child — a block, not a stored composition — which keeps the JSON boundary
   * intact: a block declares a named rule in its props and turns it into this
   * function itself.
   */
  labelFromData?: (plotted: BaseDataPoint[], series: AssembledSeries) => string;
  partialCoverageLabel?: string;
}

interface Viewport {
  startMs: number;
  endMs: number;
}

const MISSING_TOLERANCE = 0.05;
const GAP_BUCKETS = 1.5;

/** Share of the window, up to the latest data, the stream's line fills: edges and gaps it would break over are missing. */
function coverageOf(
  series: AssembledSeries,
  key: string,
  viewport: Viewport,
  maxDataTimeMs: number = viewport.endMs
): "none" | "partial" | "full" {
  const times = pointsFor(series, key)
    .map((p) => p.xValue.getTime())
    .filter((t) => t >= viewport.startMs && t < viewport.endMs)
    .sort((a, b) => a - b);
  if (times.length === 0) return "none";
  const end = Math.min(viewport.endMs, maxDataTimeMs);
  const bucketMs = series.renderTier?.bucketMs ?? 0;
  let missing =
    Math.max(0, (times[0] ?? 0) - viewport.startMs) + Math.max(0, end - ((times[times.length - 1] ?? 0) + bucketMs));
  for (let i = 1; i < times.length; i++) {
    const gap = (times[i] ?? 0) - (times[i - 1] ?? 0);
    if (gap > bucketMs * GAP_BUCKETS) missing += gap - bucketMs;
  }
  return missing > (end - viewport.startMs) * MISSING_TOLERANCE ? "partial" : "full";
}

function partialCoverageNotes(
  children: ReactNode,
  series: AssembledSeries,
  viewport: Viewport,
  maxDataTimeMs: number
): string[] {
  const labels = flattenChartChildren(children).flatMap((child) => {
    if (!isValidElement(child)) return [];
    const props = child.props as SeriesChildProps;
    if (props.data || !props.partialCoverageLabel) return [];
    return coverageOf(series, props.seriesKey ?? DEFAULT_SERIES_KEY, viewport, maxDataTimeMs) === "partial"
      ? [props.partialCoverageLabel]
      : [];
  });
  return [...new Set(labels)];
}

function declaresPartialCoverage(children: ReactNode): boolean {
  return flattenChartChildren(children).some(
    (child) => isValidElement(child) && Boolean((child.props as SeriesChildProps).partialCoverageLabel)
  );
}

/**
 * Resolve one series child's points out of the assembled streams.
 *
 * `line` and `band` are the two the engine names itself; everything else lands
 * in `lines` under its own key. A child naming a stream that this source does
 * not produce draws nothing rather than throwing — a chart asking for an export
 * series from a meter that has none is a normal state, not a bug.
 */
function pointsFor(series: AssembledSeries, key: string): BaseDataPoint[] {
  if (key === DEFAULT_SERIES_KEY) return series.line;
  if (key === "band") return series.band;
  return series.lines[key] ?? [];
}

/**
 * The smallest bucket the ladder offers, or 0 when it is empty.
 *
 * Order-independent on purpose — see the call site.
 */
function finestBucketMs(ladder: StreamingChartSource["ladder"]): number {
  let finest = 0;
  for (const tier of ladder) {
    if (typeof tier?.bucketMs === "number" && Number.isFinite(tier.bucketMs) && tier.bucketMs > 0) {
      finest = finest === 0 ? tier.bucketMs : Math.min(finest, tier.bucketMs);
    }
  }
  return finest;
}

export interface StreamingChartProps {
  source: StreamingChartSource;
  title: string;
  children: ReactNode;
  yFormatter?: React.ComponentProps<typeof TiledTimeSeriesExplorer>["yFormatter"];
  yDomain?: React.ComponentProps<typeof TiledTimeSeriesExplorer>["yDomain"];
  referenceLines?: React.ComponentProps<typeof TiledTimeSeriesExplorer>["referenceLines"];
  headerActions?: ReactNode;
  height?: number;
  hint?: string;
  emptyState?: { title: string; description: string };
  tierToWindow?: React.ComponentProps<typeof TiledTimeSeriesExplorer>["tierToWindow"];
  windowSizeOptions?: React.ComponentProps<typeof TiledTimeSeriesExplorer>["windowSizeOptions"];
  presetRanges?: React.ComponentProps<typeof TiledTimeSeriesExplorer>["presetRanges"];
  /** Built-in "latest data in view" readout, selected by name. */
  latestDataReadout?: React.ComponentProps<typeof TiledTimeSeriesExplorer>["latestDataReadout"];
  /** Which streams that readout inspects, when the defaults do not fit. */
  latestDataSeriesKeys?: string[];
  /**
   * Extra plots stacked below this one, sharing its camera and its controls.
   *
   * Each declares its OWN streaming source — a different ref, a different
   * ladder, its own cost — and each resolves through the same host, because a
   * stack of telemetry panels is several streams of one node rather than
   * several series of one stream.
   *
   * Omit it and this is the single-plot chart it has always been.
   */
  panels?: StreamingChartPanel[];
}

/**
 * One extra plot in a streaming stack, declared the same way the primary is.
 *
 * `children` is the same `seriesKey`-naming form the primary takes, for the
 * same reason: a block carries JSON, so a panel names its streams rather than
 * holding a render-prop.
 *
 * Module-private: callers reach this shape through
 * `ChartContainer`'s `streaming.panels`, which derives it from
 * `ComponentProps<typeof StreamingChart>`. An exported name nothing imports is
 * what knip fails CI on, and the derivation means there is nothing to import.
 */
interface StreamingChartPanel {
  key: string;
  title: string;
  source: StreamingChartSource;
  children: ReactNode;
  yFormatter?: React.ComponentProps<typeof TiledTimeSeriesExplorer>["yFormatter"];
  yDomain?: React.ComponentProps<typeof TiledTimeSeriesExplorer>["yDomain"];
  referenceLines?: React.ComponentProps<typeof TiledTimeSeriesExplorer>["referenceLines"];
  height?: number;
}

/**
 * Draws a chart whose data streams in as the reader moves through it.
 *
 * ── What this is, and what it is not ──────────────────────────────────────────
 * This is the streaming BRANCH of `ChartContainer`, not a component callers
 * reach for directly. `ChartContainer` picks it when handed a streaming source,
 * which is the whole point: the choice of loading strategy stops being a choice
 * of import.
 *
 * It delegates to `TiledTimeSeriesExplorer` rather than reimplementing it. That
 * engine already owns the camera, the resolution ladder, the fetch budget and
 * the slice cache, and all of that is correct — the problem was never its
 * behaviour, only that it was reachable by one import and not by a prop. So this
 * file is an adapter, deliberately thin, and the convergence of the two render
 * engines is a later step that this one does not prejudge.
 *
 * ── The one translation it performs ───────────────────────────────────────────
 * The explorer takes a render-prop (`{series} => ReactNode`) — a function, which
 * is exactly what a block cannot express. So series children name their stream
 * with `seriesKey` and this clones them with the resolved points. Declarative in,
 * render-prop out, with the function living here instead of at every call site.
 */
export function StreamingChart(props: StreamingChartProps) {
  const Host = useChartSourceHost();

  /*
   * 🔴 RENDER the host, never call it.
   *
   * `host({ source, children })` looks equivalent and is not. A plain call
   * creates no component, so every hook the host body uses joins
   * `StreamingChart`'s OWN hook list — and because the host is optional, that
   * list changes length the moment a host is installed or removed. React then
   * reports "a change in the order of Hooks called by StreamingChart" and the
   * two renders disagree about which state belongs to which hook.
   *
   * Rendering it as `<Host>` gives the host its own fiber and therefore its own
   * hook list, which is the entire reason this half of the contract is a
   * component rather than a function. Holding hooks is the job; a call cannot do
   * it.
   */
  if (Host) {
    return (
      <Host source={props.source}>
        {(resolved) =>
          // `null` from a host is "not mine" — fall through to the plain resolver
          // before giving up, so an app can register both halves and let each
          // answer the refs it knows.
          resolved ? <WithPanels {...props} resolved={resolved} /> : <StreamingChartResolved {...props} />
        }
      </Host>
    );
  }
  return <StreamingChartResolved {...props} />;
}

/**
 * The primary source is resolved; now resolve the panels, if there are any.
 *
 * Short-circuits entirely when there are none, so a single-plot chart renders
 * exactly the component tree it did before panels existed — no extra fibers, no
 * extra hooks, nothing to regress.
 */
function WithPanels(props: StreamingChartProps & { resolved: ResolvedChartSource }) {
  const panels = props.panels;
  if (!panels || panels.length === 0) return <StreamingChartPlot {...props} />;
  return (
    <ResolvedPanels
      panels={panels}
      index={0}
      acc={[]}
      render={(resolvedPanels) => <StreamingChartPlot {...props} resolvedPanels={resolvedPanels} />}
    />
  );
}

/** The plain-function half of the contract. Unchanged from M1. */
function StreamingChartResolved(props: StreamingChartProps) {
  const resolve = useChartSourceResolver();
  const resolved = useMemo(() => resolve?.(props.source) ?? null, [resolve, props.source]);

  // No provider, or a ref this app does not know. Both are ordinary states — a
  // board tile whose source was retired, a chart in a docs page with no data
  // layer — and both want the same quiet answer rather than a crash.
  if (!resolved) {
    return (
      <EmptyState
        icon="ChartLine"
        title={props.emptyState?.title ?? "No data source"}
        description={props.emptyState?.description ?? "This chart's data source is unavailable."}
      />
    );
  }
  return <WithPanels {...props} resolved={resolved} />;
}

function StreamingChartPlot({
  source,
  title,
  children,
  yFormatter,
  yDomain,
  referenceLines,
  headerActions,
  height,
  hint,
  emptyState,
  tierToWindow,
  windowSizeOptions,
  presetRanges,
  latestDataReadout,
  latestDataSeriesKeys,
  resolved,
  resolvedPanels = [],
}: StreamingChartProps & {
  resolved: ResolvedChartSource;
  /**
   * Panels whose sources the host has already answered.
   *
   * Resolved ABOVE this component rather than here, because resolving is a
   * hook-holding job and React forbids running hooks in a loop — one panel
   * added or removed would change this component's hook list and React would
   * pair the wrong state with the wrong panel. `ResolvedPanels` walks them one
   * component at a time instead. See its note.
   */
  resolvedPanels?: TiledTimeSeriesPanel[];
}) {
  /*
   * The time-travel anchor, parsed once.
   *
   * 🔴 An unparseable string must read as NO anchor, not as an invalid Date.
   * `new Date("nonsense")` yields NaN, and a NaN anchor propagates into every
   * window calculation downstream — the chart would render an empty plot with
   * no error anywhere. A malformed value is a broken declaration, and the
   * honest answer to one is the chart's ordinary live behaviour.
   */
  const anchor = parseInstant(source.viewportEnd);

  /*
   * The page-wide fetch ceiling, applied at the ONE seam every streaming chart
   * passes through.
   *
   * ── 🔴 Why here and not in the loader ─────────────────────────────────────
   * `useTileLoader` already bounds concurrency, but its in-flight map is a ref
   * inside a single hook instance, so the bound is per chart: one chart asks
   * for 3 slices at a time, twelve charts ask for 36, and nothing can see the
   * total. The budget has to live somewhere SHARED, and this is the only point
   * where every chart — in any package, streaming any source — meets.
   *
   * Wrapping the fetcher rather than teaching the loader about a budget keeps
   * the whole mechanism invisible: no source, block or host learns it exists,
   * which is what lets blocks added later pick it up for free.
   *
   * The one thing a resolver DOES have to say is `fetchesPerSlice`, when its
   * slice fans out into several requests — a count only the resolver can know,
   * and the difference between a ceiling of 12 and 36 on a board of cohort
   * charts. See the note on that field.
   */
  const budget = useChartFetchBudget();
  const budgetedFetch = useMemo(
    () => withFetchBudget(budget, resolved.fetchSlice, resolved.fetchesPerSlice ?? 1),
    [budget, resolved.fetchSlice, resolved.fetchesPerSlice]
  );

  const explorerSource = {
    fetchTile: budgetedFetch,
    ladder: source.ladder,
    /*
     * 🔴 The host's answer wins over the declaration, and only when it gave one.
     *
     * `refreshEnabled` reads `!== false` downstream, so writing `undefined` here
     * is the same as `true` — which is exactly the wrong default for a chart
     * whose host offers a Static toggle. A `??` chain would be fine; an
     * unconditional spread of a possibly-absent field would silently re-enable
     * refreshing the moment the host stops supplying it.
     */
    ...(resolved.refreshEnabled !== undefined ? { refreshEnabled: resolved.refreshEnabled } : {}),
    // The FINEST bucket, not `ladder[0]`. `nativeMs` is a lower floor on tier
    // selection, so taking the first entry pins the chart to whatever end the
    // ladder happens to be written from — and both orderings exist in this repo
    // (the contract documents coarsest-to-finest; the selector fixtures run
    // fine-to-coarse). Reading the minimum is order-independent and cannot
    // accidentally exclude every finer tier from ever being fetched.
    nativeMs: resolved.nativeMs ?? source.nativeMs ?? finestBucketMs(source.ladder),
    resetKey: source.resetKey ?? "",
    // A live host derives this from a delivered signal, so its value outranks
    // whatever the declaration guessed.
    ...(resolved.refreshKey !== undefined
      ? { refreshKey: resolved.refreshKey }
      : source.refreshKey !== undefined
        ? { refreshKey: source.refreshKey }
        : {}),
    tileCostSeriesCount: resolved.costSeriesCount ?? 1,
  };

  return (
    <TiledTimeSeriesExplorer
      title={title}
      source={explorerSource}
      yFormatter={yFormatter}
      {...(yDomain !== undefined ? { yDomain } : {})}
      {...(referenceLines !== undefined ? { referenceLines } : {})}
      {...(headerActions !== undefined ? { headerActions } : {})}
      /*
       * 🔴 Fill the box the caller reserved rather than adding to it.
       *
       * `ChartContainer` is measured by its parent, and every caller sizes the
       * element around it — `UsageBlock` reserves 280px. The explorer defaults
       * its PLOT alone to 360px and renders time controls and a readout above
       * it, so an unfitted streaming chart inside a 280px box overflows and
       * overlaps whatever follows. Honour an explicit `height` when one is
       * given; otherwise fit.
       */
      {...(height !== undefined ? { height } : { fitParent: true })}
      /* Host-supplied live surface — see `ResolvedChartSource`. */
      {...(resolved.liveControl !== undefined ? { liveControl: resolved.liveControl } : {})}
      {...(resolved.onViewChange !== undefined ? { onViewChange: resolved.onViewChange } : {})}
      {...(resolved.followLatest !== undefined ? { followLatest: resolved.followLatest } : {})}
      {...(resolved.followLatestKey !== undefined ? { followLatestKey: resolved.followLatestKey } : {})}
      {...(hint !== undefined ? { hint } : {})}
      {...(source.initialRange !== undefined ? { initialRange: source.initialRange } : {})}
      {...(anchor !== null ? { viewportEnd: anchor } : {})}
      {...(presetRanges !== undefined ? { presetRanges } : {})}
      {...(latestDataReadout !== undefined ? { latestDataReadout } : {})}
      {...(latestDataSeriesKeys !== undefined ? { latestDataSeriesKeys } : {})}
      emptyState={emptyState ?? { title: "No data", description: "No readings in this window." }}
      tierToWindow={tierToWindow ?? {}}
      windowSizeOptions={windowSizeOptions ?? []}
      {...(resolvedPanels.length > 0 ? { panels: resolvedPanels } : {})}
      {...(declaresPartialCoverage(children)
        ? {
            renderMetadata: ({
              series,
              viewport,
              maxDataTimeMs,
            }: {
              series: AssembledSeries;
              viewport: Viewport;
              maxDataTimeMs: number;
            }) =>
              partialCoverageNotes(children, series, viewport, maxDataTimeMs).map((label) => (
                <span key={label} className="text-text-tertiary">
                  {label} available for part of this range
                </span>
              )),
          }
        : {})}
    >
      {({ series, gapMs, viewport }) => bindSeriesChildren(children, series, gapMs, viewport, true)}
    </TiledTimeSeriesExplorer>
  );
}

/**
 * Resolves each panel's source, then renders the plot with all of them in hand.
 *
 * ── 🔴 Why this recurses instead of looping ───────────────────────────────────
 * Resolving a source means rendering the HOST (it holds hooks — that is the
 * whole reason `ChartSourceHost` is a component). Hooks cannot run in a loop:
 * adding or removing one panel would change the hook list of whatever component
 * did the looping, and React would pair the wrong state with the wrong panel —
 * or refuse to render at all.
 *
 * So the panels are resolved one at a time, each in its own fiber, and the
 * accumulated results are handed down. The recursion depth is the panel count
 * (six metric groups at the high end), which is nothing, and it buys a stable
 * hook list per component no matter how the list changes.
 *
 * A panel the host declines is DROPPED rather than rendered empty: a node that
 * does not report tap position should show no tap-position panel, not an
 * permanently blank one. This mirrors `ScadaTiledCharts`, which skips a group
 * with no fetch tile.
 */
function ResolvedPanels({
  panels,
  index,
  acc,
  render,
}: {
  panels: StreamingChartPanel[];
  index: number;
  acc: TiledTimeSeriesPanel[];
  render: (resolved: TiledTimeSeriesPanel[]) => ReactNode;
}) {
  if (index >= panels.length) return <>{render(acc)}</>;
  const panel = panels[index];
  return (
    <PanelSource panel={panel}>
      {(resolvedPanel) => (
        <ResolvedPanels
          panels={panels}
          index={index + 1}
          acc={resolvedPanel ? [...acc, resolvedPanel] : acc}
          render={render}
        />
      )}
    </PanelSource>
  );
}

/** One panel's source, resolved through the same host the primary plot uses. */
function PanelSource({
  panel,
  children,
}: {
  panel: StreamingChartPanel;
  children: (resolved: TiledTimeSeriesPanel | null) => ReactNode;
}) {
  const Host = useChartSourceHost();
  if (Host) {
    return (
      <Host source={panel.source}>
        {(resolved) =>
          resolved ? (
            <PanelResolved panel={panel} resolved={resolved}>
              {children}
            </PanelResolved>
          ) : (
            <PanelViaResolver panel={panel}>{children}</PanelViaResolver>
          )
        }
      </Host>
    );
  }
  return <PanelViaResolver panel={panel}>{children}</PanelViaResolver>;
}

/** The plain-resolver half, for a panel no host answered. */
function PanelViaResolver({
  panel,
  children,
}: {
  panel: StreamingChartPanel;
  children: (resolved: TiledTimeSeriesPanel | null) => ReactNode;
}) {
  const resolve = useChartSourceResolver();
  const resolved = useMemo(() => resolve?.(panel.source) ?? null, [resolve, panel.source]);
  if (!resolved) return <>{children(null)}</>;
  return (
    <PanelResolved panel={panel} resolved={resolved}>
      {children}
    </PanelResolved>
  );
}

/** Turns a declared panel plus its resolved source into the explorer's shape. */
function PanelResolved({
  panel,
  resolved,
  children,
}: {
  panel: StreamingChartPanel;
  resolved: ResolvedChartSource;
  children: (resolved: TiledTimeSeriesPanel | null) => ReactNode;
}) {
  // The same page-wide ceiling the primary plot passes through — a panel's
  // fetches are no cheaper, and a stack of six is exactly the fan-out the
  // budget exists to bound.
  const budget = useChartFetchBudget();
  const budgetedFetch = useMemo(
    () => withFetchBudget(budget, resolved.fetchSlice, resolved.fetchesPerSlice ?? 1),
    [budget, resolved.fetchSlice, resolved.fetchesPerSlice]
  );

  const explorerPanel = useMemo<TiledTimeSeriesPanel>(
    () => ({
      key: panel.key,
      title: panel.title,
      source: {
        fetchTile: budgetedFetch,
        ladder: panel.source.ladder,
        nativeMs: resolved.nativeMs ?? panel.source.nativeMs ?? finestBucketMs(panel.source.ladder),
        resetKey: panel.source.resetKey ?? "",
        tileCostSeriesCount: resolved.costSeriesCount ?? 1,
        ...(resolved.refreshEnabled !== undefined ? { refreshEnabled: resolved.refreshEnabled } : {}),
        ...(resolved.refreshKey !== undefined
          ? { refreshKey: resolved.refreshKey }
          : panel.source.refreshKey !== undefined
            ? { refreshKey: panel.source.refreshKey }
            : {}),
      },
      ...(panel.yFormatter !== undefined ? { yFormatter: panel.yFormatter } : {}),
      ...(panel.yDomain !== undefined ? { yDomain: panel.yDomain } : {}),
      ...(panel.referenceLines !== undefined ? { referenceLines: panel.referenceLines } : {}),
      ...(panel.height !== undefined ? { height: panel.height } : {}),
      children: ({ series, gapMs, viewport }) => bindSeriesChildren(panel.children, series, gapMs, viewport, false),
    }),
    [panel, budgetedFetch, resolved.nativeMs, resolved.costSeriesCount, resolved.refreshEnabled, resolved.refreshKey]
  );

  return <>{children(explorerPanel)}</>;
}

/**
 * The declarative→render-prop translation, shared by the primary plot and every
 * panel.
 *
 * Extracted rather than duplicated because a panel's children are declared
 * exactly like the primary's — they name a stream with `seriesKey` — so the two
 * must resolve identically. Two copies would drift on the next field, and the
 * symptom would be a panel silently ignoring something the primary honours.
 */
function bindSeriesChildren(
  children: ReactNode,
  series: AssembledSeries,
  gapMs: number,
  viewport: Viewport,
  optionalCoverage: boolean
): ReactNode {
  return flattenChartChildren(children).map((child) => {
    if (!isValidElement(child)) return child;
    const props = child.props as SeriesChildProps;
    if (
      optionalCoverage &&
      !props.data &&
      props.partialCoverageLabel &&
      coverageOf(series, props.seriesKey ?? DEFAULT_SERIES_KEY, viewport) === "none"
    ) {
      return null;
    }
    // A child that brought its own data keeps it. Mixing a fed series into
    // a streaming chart is legitimate — a static threshold line over live
    // telemetry — and silently replacing its points would be wrong.
    if (props.data) return child;
    const points = pointsFor(series, props.seriesKey ?? DEFAULT_SERIES_KEY);
    return cloneElement(child as ReactElement<SeriesChildProps & { maxGapMs?: number }>, {
      data: points,
      maxGapMs: gapMs,
      // Resolved here because this is the only scope holding every
      // assembled stream; `labelFromData` itself never leaves the caller.
      ...(props.labelFromData ? { label: props.labelFromData(points, series) } : {}),
    });
  });
}
